/**
 * The notes column and the annotation scale.
 *
 * A callout with no authored head is stacked in a column beside the drawing,
 * on its target's side, in target order (so leaders never cross), level with
 * its target where it can be. Authored heads pass through.
 *
 * Annotation is screen-constant (k = 1/zoom) until a placed note would touch
 * the drawing or another note; k is capped there, so from then on the sheet
 * shrinks as one. Column notes never touch anything, so they never cap.
 */
import type { PageBox, PagePoint } from "./geometry";

export interface CalloutPlacement {
  id: string;
  /** Authored head in page units; null asks the sheet to place it. */
  head: PagePoint | null;
  /** Where the leader lands, page units. */
  target: PagePoint;
  /** The block, in screen pixels, relative to the head: how far it runs
   *  left and right of it (lettering runs one way from the head, a tag
   *  is centred), from the head up to its top edge (half a line for
   *  lettering, the radius for a tag), and its height. */
  left: number;
  right: number;
  above: number;
  height: number;
}

export interface AnnotationScale {
  /** Page units per screen pixel. */
  k: number;
  /** The scale at which placed notes would touch the drawing or each other;
   *  Infinity when nothing is placed where it could. */
  cap: number;
}

/** Air between stacked notes, and between a column and the drawing, in
 *  screen pixels. The margin clears the leader's landing. */
const GAP = 8;
const MARGIN = 28;

/** The sheet's annotation scale: 1/zoom, capped where placed notes would
 *  interfere with the drawing or each other. */
export function annotationScale(
  callouts: readonly CalloutPlacement[],
  drawing: readonly PageBox[],
  zoom: number,
): AnnotationScale {
  const cap = scaleCap(callouts, drawing);
  return { k: Math.min(1 / zoom, cap), cap };
}

/** Every sheet-placed head, page units, at annotation scale `k`. Authored
 *  heads are absent: the shape carries those. */
export function placeCallouts(
  callouts: readonly CalloutPlacement[],
  drawing: readonly PageBox[],
  k: number,
): Map<string, PagePoint> {
  const box = drawingBox(drawing);
  const heads = new Map<string, PagePoint>();
  for (const [side, column] of columnsOf(callouts, box)) {
    const x = side === 1 ? box.x + box.w + MARGIN * k : box.x - MARGIN * k;
    for (const [id, y] of stackColumn(column, box, k)) {
      heads.set(id, { x, y });
    }
  }
  return heads;
}

function drawingBox(drawing: readonly PageBox[]): PageBox {
  if (!drawing.length) return { x: 0, y: 0, w: 0, h: 0 };
  const x = Math.min(...drawing.map((b) => b.x));
  const y = Math.min(...drawing.map((b) => b.y));
  return {
    x,
    y,
    w: Math.max(...drawing.map((b) => b.x + b.w)) - x,
    h: Math.max(...drawing.map((b) => b.y + b.h)) - y,
  };
}

/** The unplaced notes, one column per side, each ordered top to bottom by
 *  target. */
function columnsOf(
  callouts: readonly CalloutPlacement[],
  box: PageBox,
): Map<1 | -1, CalloutPlacement[]> {
  const center = box.x + box.w / 2;
  const columns = new Map<1 | -1, CalloutPlacement[]>();
  for (const callout of callouts) {
    if (callout.head) continue;
    const side = callout.target.x >= center ? 1 : -1;
    const column = columns.get(side) ?? [];
    columns.set(side, column);
    column.push(callout);
  }
  for (const column of columns.values()) {
    column.sort((a, b) => a.target.y - b.target.y);
  }
  return columns;
}

/** Heads down the column: level with the target where possible, pushed
 *  down below the note above otherwise, then pushed back up from the
 *  drawing's foot so the column ends where the drawing does. */
function stackColumn(
  column: CalloutPlacement[],
  box: PageBox,
  k: number,
): Map<string, number> {
  const ys: number[] = [];
  let floor = -Infinity;
  for (const callout of column) {
    const y = Math.max(callout.target.y, floor + callout.above * k);
    ys.push(y);
    floor = y - callout.above * k + (callout.height + GAP) * k;
  }
  let ceiling = box.y + box.h;
  for (let i = column.length - 1; i >= 0; i--) {
    const callout = column[i]!;
    const y = Math.min(ys[i]!, ceiling - (callout.height - callout.above) * k);
    ys[i] = y;
    ceiling = y - (callout.above + GAP) * k;
  }
  return new Map(column.map((callout, i) => [callout.id, ys[i]!]));
}

/** A coordinate that moves with the scale: `c + m*k`. */
interface Lin {
  c: number;
  m: number;
}

/** An extent along one axis, each end linear in the scale: a placed block
 *  is anchored at its head and sized in pixels, the drawing is fixed. */
interface Span {
  min: Lin;
  max: Lin;
}

interface Block {
  x: Span;
  y: Span;
}

/** The largest scale at which no placed note touches the drawing or
 *  another placed note. A pair that overlaps at every scale (a head inside
 *  the drawing, where the drafter put it) does not constrain. */
function scaleCap(
  callouts: readonly CalloutPlacement[],
  drawing: readonly PageBox[],
): number {
  const placed = callouts.flatMap((c) => (c.head ? [blockOf(c, c.head)] : []));
  const fixed = drawing.map((b): Block => ({
    x: { min: { c: b.x, m: 0 }, max: { c: b.x + b.w, m: 0 } },
    y: { min: { c: b.y, m: 0 }, max: { c: b.y + b.h, m: 0 } },
  }));
  let cap = Infinity;
  placed.forEach((block, i) => {
    for (const other of [...fixed, ...placed.slice(i + 1)]) {
      cap = Math.min(cap, overlapStart(block, other) ?? Infinity);
    }
  });
  return cap;
}

function blockOf(c: CalloutPlacement, head: PagePoint): Block {
  return {
    x: { min: { c: head.x, m: -c.left }, max: { c: head.x, m: c.right } },
    y: {
      min: { c: head.y, m: -c.above },
      max: { c: head.y, m: c.height - c.above },
    },
  };
}

/** The scale at which two blocks begin to overlap, or null when they never
 *  do or always do. Each edge-vs-edge condition is linear in k, so the
 *  scales at which the blocks overlap form one interval; its lower end is
 *  where they touch. */
function overlapStart(a: Block, b: Block): number | null {
  const x = overlapInterval(a.x, b.x);
  const y = overlapInterval(a.y, b.y);
  if (!x || !y) return null;
  const lo = Math.max(x.lo, y.lo);
  const hi = Math.min(x.hi, y.hi);
  if (lo <= 0 || lo >= hi) return null;
  return lo;
}

/** Scales k > 0 at which two spans overlap, as an interval, or null. */
function overlapInterval(a: Span, b: Span): { lo: number; hi: number } | null {
  let lo = 0;
  let hi = Infinity;
  // a.min < b.max and b.min < a.max, each `m*k < d` for some m and d.
  for (const [near, far] of [
    [a.min, b.max],
    [b.min, a.max],
  ] as const) {
    const m = near.m - far.m;
    const d = far.c - near.c;
    if (m < 0) lo = Math.max(lo, d / m);
    else if (m > 0) hi = Math.min(hi, d / m);
    else if (d <= 0) return null;
  }
  return lo < hi ? { lo, hi } : null;
}
